import React, { useState } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { color } from '@trivie/ui-web/theme'
import { Delete, DragIndicator } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { Text } from '@trivie/ui-web/components/text'
import { getNodeId, Instance } from 'mobx-state-tree'
import { QuestionChoice } from '@trivie/core/src/models/QuestionChoice'

export const OrderedChoices = observer((props: any) => {
  const { question } = props

  const [hovered, setHovered] = useState<any>()
  const [grabbing, setGrabbing] = useState(false)
  const [dropChoiceID, setDropChoiceID] = useState<any>()
  const [draggingChoiceID, setDraggingChoiceID] = useState<any>()
  const [hoveredID, setHoveredID] = useState<any>()

  const ordering = question.choices[0]?.ordering

  const handleDrag = (choice: Instance<typeof QuestionChoice>) =>
    setDraggingChoiceID(getNodeId(choice))

  const handleDragOver = (choice: Instance<typeof QuestionChoice>) =>
    setDropChoiceID(getNodeId(choice))

  const reorderOrdering = (drag: number, drop: number) => {
    const temp = ordering?.slice()
    if (temp) {
      temp.splice(drop, 0, temp.splice(drag, 1)[0])
      question.choices[0].setOrdering(temp)
    }
    question.setTouched(true)
    question.setApproved(false)
  }

  const handleDrop = (orderedChoice: any) => {
    setDropChoiceID(null)
    setGrabbing(false)

    const dragChoice = ordering?.find((c) => getNodeId(c) === draggingChoiceID)
    const dropChoice = ordering?.find((c) => getNodeId(c) === getNodeId(orderedChoice))

    if (dragChoice && dropChoice) {
      reorderOrdering(ordering.indexOf(dragChoice) ?? 0, ordering.indexOf(dropChoice) ?? 0)
    }
  }

  const textSx = {
    '& .MuiInputBase-root': {
      textDecoration: 'none',
      fontSize: 14,
      fontFamily: 'Inter',
      fontWeight: 500,
      paddingRight: '80px',
      lineHeight: '16.94px',
      paddingTop: '8px',
      paddingBottom: '8px',
      pointerEvents: 'auto',
    },
  }

  return ordering?.map((orderedChoice, index) => {
    return (
      <div
        style={{
          ...PAIR_CONTAINER,
          backgroundColor:
            dropChoiceID === getNodeId(orderedChoice) && grabbing ? color.shade10 : color.white,
        }}
        draggable={grabbing}
        onDragOver={() => handleDragOver(orderedChoice)}
        onDrag={() => handleDrag(orderedChoice)}
        onDragEnd={(e) => setGrabbing(false)}
        onDrop={() => handleDrop(orderedChoice)}
      >
        <TextField
          onMouseEnter={() => setHovered(orderedChoice)}
          onMouseLeave={() => setHovered(null)}
          sx={{
            ...textSx,
            '& .MuiInput-underline:after': {
              borderBottomColor:
                orderedChoice?.touched && orderedChoice?.formErrors?.text ? color.red : color.blue,
            },
            '& .MuiInput-underline:before': {
              borderBottomColor:
                orderedChoice?.touched && orderedChoice?.formErrors?.text
                  ? color.red
                  : color.shade10,
            },
          }}
          multiline
          style={{ width: '100%' }}
          value={orderedChoice.text}
          variant="standard"
          placeholder={`Answer ${index + 1}`}
          inputProps={{ 'aria-label': 'Answer 1' }}
          onChange={(e) => {
            orderedChoice.setTouched(true)
            orderedChoice.setText(String(e.target.value))
          }}
          InputProps={{
            startAdornment: (
              <div
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  display: 'flex',
                  gap: 12,
                  marginRight: index === 0 ? 36 : 34,
                }}
              >
                <DragIndicator
                  onMouseLeave={() => setHoveredID(null)}
                  onMouseEnter={() => setHoveredID(getNodeId(orderedChoice))}
                  onMouseDown={() => setGrabbing(true)}
                  onMouseUp={() => {
                    setGrabbing(false)
                    setDropChoiceID(null)
                  }}
                  style={{
                    ...HANDLE,
                    color: hoveredID === getNodeId(orderedChoice) ? color.shade70 : color.shade10,
                    cursor: grabbing ? 'grabbing' : 'grab',
                  }}
                />
                <Text
                  variant="body1Medium"
                  style={{ color: color.shade50 }}
                  text={`${index + 1}.`}
                />
              </div>
            ),
            endAdornment: (
              <InputAdornment position="end" style={{ position: 'absolute', right: 0 }}>
                <div style={{ flexDirection: 'row', cursor: 'pointer' }}>
                  {ordering.length > 2 && hovered === orderedChoice && (
                    <IconButton
                      sx={{
                        '&:hover': {
                          svg: { color: color.shade50, transition: 'color .5s' },
                        },
                      }}
                      style={{ marginRight: 2 }}
                      onClick={() => orderedChoice.delete()}
                    >
                      <Delete style={{ fontSize: 18 }} htmlColor={color.shade30} />
                    </IconButton>
                  )}
                </div>
              </InputAdornment>
            ),
          }}
        />
      </div>
    )
  })
})

const PAIR_CONTAINER: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
}
const HANDLE: React.CSSProperties = {
  color: color.shade10,
  alignSelf: 'center',
  fontSize: 20,
}
